import { ChecklistFieldValue } from "@smartsuite/types";
import React from "react";

import { $t } from "../../../../utils/intl";
import { ReviewFieldProps } from "../../../types";
import { isChecklistAssigneeEnabled, isChecklistDueDateEnabled } from "../checklistFormOptions";
import { useChecklistAssignees } from "../useChecklistAssignees";
import { buildChecklistReviewRows } from "./checklistReviewValue.helpers";
import { checklistReviewValueText } from "./ChecklistReviewValue.text";

import "./ChecklistReviewValue.sass";

export type ChecklistReviewValueProps = ReviewFieldProps<ChecklistFieldValue | undefined>;

/**
 * The checklist's value on the review page: one row per item with its completion answer, and
 * the item's assignee and due date underneath. A checklist is the only multi-part value on the
 * page, so it renders markup through the registry's `reviewControl` instead of the single line
 * of text every other field returns from `toReviewString`.
 */
export const ChecklistReviewValue: React.FunctionComponent<ChecklistReviewValueProps> = ({
  appField,
  formItem,
  value,
}) => {
  const items = value?.items ?? [];
  const isAssigneeEnabled = isChecklistAssigneeEnabled(formItem);
  const isDueDateEnabled = isChecklistDueDateEnabled(formItem);

  // Only ask for names when a name is actually going to be printed. The input control fetched
  // the same query keys while the respondent was filling the field in, so for a form that was
  // just answered this resolves from the cache without a second request.
  const { getAssigneeName } = useChecklistAssignees({
    fieldSlug: appField.slug,
    enabled: isAssigneeEnabled && items.some((item) => !!item.assignee),
  });

  const rows = buildChecklistReviewRows({
    getAssigneeName,
    isAssigneeEnabled,
    isDueDateEnabled,
    items,
  });

  if (!rows.length) return null;

  return (
    <ul className="checklist-review-value" data-testid="checklist-review-value">
      {rows.map((row) => (
        <li className="checklist-review-value__item" key={row.id}>
          <div className="checklist-review-value__body">
            <span className="checklist-review-value__text">{row.text}</span>

            {(!!row.assigneeName || !!row.dueDate) && (
              <span className="checklist-review-value__meta">
                {!!row.assigneeName && (
                  <span>
                    {$t(checklistReviewValueText.assignedTo, { value: row.assigneeName })}
                  </span>
                )}
                {!!row.dueDate && (
                  <span>{$t(checklistReviewValueText.dueDate, { value: row.dueDate })}</span>
                )}
              </span>
            )}
          </div>

          <span className="checklist-review-value__state">
            {row.completed ? $t(checklistReviewValueText.yes) : $t(checklistReviewValueText.no)}
          </span>
        </li>
      ))}
    </ul>
  );
};
